import { environment } from 'environments/environment';
import apiService from 'services/ApiService';
import { logger } from 'utils/logger';

// Create a service-specific logger
const log = logger.getLogger('AlertsService');

const baseUrl = environment.services.account.baseUrl;
const alertsUrl = baseUrl + environment.services.account.basePath;

export interface User {
  id: number;
  name: string;
}

export interface Alert {
  id: number;
  message: string;
  site: boolean;
  hub: boolean;
  dashboards: number[] | null;
  createdBy: User;
  endedBy: User | null;
  createDate: string;
  endedDate: string | null;
}

export interface CreateAlertPayload {
  message: string;
  site?: boolean;
  hub?: boolean;
  dashboards?: number[];
}

export interface AlertsResponse {
  count: number;
  alerts: Alert[];
}

class AlertsService {
  /**
   * Fetch all alerts
   * @param showAll - Whether to show all alerts (including ended ones)
   * @returns AlertsResponse containing count and alerts array
   */
  async getAlerts(showAll: boolean = true): Promise<AlertsResponse> {
    try {
      const apiUrl = `${alertsUrl}/alerts?showAll=${showAll}`;
      
      log.log('Fetching alerts with URL:', apiUrl);
      
      const response = await apiService.makeRequest('get', apiUrl);

      return response as AlertsResponse;
    } catch (error) {
      log.error('Error fetching alerts:', error);
      throw error;
    }
  }

  /**
   * Create a new alert
   * @param payload - Alert creation payload
   * @returns AlertsResponse with the newly created alert
   */
  async createAlert(payload: CreateAlertPayload): Promise<AlertsResponse> {
    try {
      const apiUrl = `${alertsUrl}/alerts`;
      
      log.log('Creating alert with payload:', payload);
      
      const response = await apiService.makeRequest('post', apiUrl, payload);
      
      // Invalidate cache for alerts list
      apiService.invalidateCacheByPattern(`/alerts`);
      
      // Validate and return response
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid API response format');
      }
      
      return response as AlertsResponse;
    } catch (error) {
      log.error('Error creating alert:', error);
      throw error;
    }
  }

  /**
   * End an existing alert
   * @param id - Alert ID to end
   * @returns AlertsResponse with the updated alert
   */
  async endAlert(id: number): Promise<AlertsResponse> {
    try {
      const apiUrl = `${alertsUrl}/alerts/${id}/end`;
      
      log.log(`Ending alert with ID: ${id}`);
      
      const response = await apiService.makeRequest('put', apiUrl);
      
      // Invalidate cache for alerts list and this specific alert
      apiService.invalidateCacheByPattern(`/alerts`);
      
      // Validate and return response
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid API response format');
      }
      
      return response as AlertsResponse;
    } catch (error) {
      log.error(`Error ending alert with ID ${id}:`, error);
      throw error;
    }
  }
}

export const alertsService = new AlertsService();
export default alertsService;
